import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { propertyApi } from '@/services/api';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { PropertyCard } from '@/components/property/PropertyCard';
import { PropertyImageGallery } from '@/components/property-detail/PropertyImageGallery';
import { PropertyContactSidebar } from '@/components/property-detail/PropertyContactSidebar';
import { PropertyDetailsSection } from '@/components/property-detail/PropertyDetailsSection';
import { KeyFactsStrip } from '@/components/property-detail/KeyFactsStrip';
import { NearbyAmenities } from '@/components/property-detail/NearbyAmenities';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState } from 'react';
import { trackView } from '@/lib/tracking';
import { pushRecentlyViewed } from '@/lib/recentlyViewed';
import {
  MapPin, Bed, Bath, Maximize, Heart, Share2, Printer,
  Flag, ChevronRight, Home, Eye, Calendar, Copy
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { MobileContactActionBar } from '@/components/property-detail/mobile/MobileContactActionBar';
import { MobileSectionTabs } from '@/components/property-detail/mobile/MobileSectionTabs';
import { MobileLeadFormSection } from '@/components/property-detail/mobile/MobileLeadFormSection';
import { MobileSafetyTips, MobileUsefulLinks, MobileReportListing } from '@/components/property-detail/mobile/MobileBottomBlocks';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice } from '@/lib/formatPrice';

export default function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const lang = i18n.language || 'en';
  const [saved, setSaved] = useState(false);

  const { data: property, isLoading } = useQuery({
    queryKey: ['property', id],
    queryFn: () => propertyApi.get(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (!property?.id) return;
    const isCommercial = (property as any).property_type === 'commercial';
    trackView({
      listingType: isCommercial ? 'commercial' : 'property',
      listingId: property.id,
      city: property.city,
    });
    pushRecentlyViewed(property.id);
  }, [property?.id]);

  const { data: similarProperties } = useQuery({
    queryKey: ['similar-properties', property?.city, property?.listing_type, id],
    queryFn: async () => {
      const { data } = await supabase
        .from('properties')
        .select('*, property_images(*)')
        .eq('status', 'active')
        .eq('city', property!.city)
        .eq('listing_type', property!.listing_type)
        .neq('id', id!)
        .limit(4);
      return data || [];
    },
    enabled: !!property,
  });

  const handleSave = async () => {
    if (!user) {
      toast({ variant: 'destructive', title: 'Please login to save properties' });
      return;
    }
    if (saved) {
      await supabase.from('favorites').delete().eq('property_id', property.id).eq('user_id', user.id);
      setSaved(false);
      toast({ title: 'Removed from saved' });
    } else {
      await supabase.from('favorites').insert({ property_id: property.id, user_id: user.id });
      setSaved(true);
      toast({ title: 'Property saved!' });
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: 'Property', url });
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied to clipboard!' });
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-6 space-y-6">
          <Skeleton className="h-[460px] rounded-xl" />
          <div className="grid md:grid-cols-[1fr_360px] gap-6">
            <div className="space-y-4">
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-40 rounded-lg" />
            </div>
            <Skeleton className="h-[500px] rounded-lg" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Property not found</h1>
          <Link to="/buy" className="text-primary hover:underline">Browse properties</Link>
        </div>
      </Layout>
    );
  }

  const isPlot = property.property_type === 'land';
  const title = typeof property.title === 'object' ? ((property.title as any)[lang] || (property.title as any).en || '') : property.title;
  const description = typeof property.description === 'object' ? ((property.description as any)[lang] || (property.description as any).en || '') : property.description;
  const images = (property.property_images || []).sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
  const { displayCurrency, rate } = useCurrency();
  const formatPrice = (price: number, currency: string) => fmtPrice(price, currency, displayCurrency, rate);
  const timeAgo = property.created_at ? formatDistanceToNow(new Date(property.created_at), { addSuffix: true }) : '';

  return (
    <Layout>
      <SEOHead
        title={`${title} — ${property.property_type} for ${property.listing_type === 'sale' ? 'Sale' : 'Rent'} in ${property.city}`}
        description={description ? String(description).slice(0, 155) : `${property.property_type} for ${property.listing_type} in ${property.city}. ${property.bedrooms ? property.bedrooms + ' bedrooms, ' : ''}${property.area_sqm ? property.area_sqm + ' m². ' : ''}Price: ${formatPrice(property.price, property.currency || 'ETB')}`}
        canonical={`/property/${id}`}
        ogType="article"
        ogImage={{ type: 'property', id: id!, badge: property.listing_type === 'sale' ? 'For Sale' : 'For Rent' }}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RealEstateListing",
          name: title,
          description: description ? String(description).slice(0, 300) : undefined,
          url: `https://ethio-real-estate-hub.lovable.app/property/${id}`,
          image: images.length > 0 ? images[0].url : undefined,
          offers: {
            "@type": "Offer",
            price: property.price,
            priceCurrency: property.currency || 'ETB',
          },
          address: {
            "@type": "PostalAddress",
            addressLocality: property.city,
            addressRegion: property.sub_city || undefined,
            addressCountry: "ET",
          },
        }}
      />
      <div className="bg-muted/50 border-b">
        <div className="container py-2.5">
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground overflow-x-auto">
            <Link to="/" className="hover:text-primary transition-colors flex items-center gap-1 shrink-0">
              <Home className="h-3 w-3" />Home
            </Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <Link to={property.listing_type === 'rent' ? '/rent' : '/buy'} className="hover:text-primary transition-colors shrink-0">
              {property.listing_type === 'sale' ? 'Buy' : 'Rent'}
            </Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <Link to={`/buy?city=${property.city}`} className="hover:text-primary transition-colors shrink-0">{property.city}</Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <span className="text-foreground truncate font-medium">{title}</span>
          </nav>
        </div>
      </div>

      {/* ================= DESKTOP LAYOUT (≥lg) ================= */}
      <div className="hidden lg:block">
        <div className="container py-6">
          {/* Image Gallery */}
          <PropertyImageGallery images={images} title={title} isFeatured={property.is_featured} />

          {/* Key Facts strip */}
          <div className="mt-5">
            <KeyFactsStrip property={property} isPlot={isPlot} />
          </div>

          {/* Header section below gallery */}
          <div className="mt-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className="text-xs capitalize">{property.listing_type === 'sale' ? t('property.forSale') : t('property.forRent')}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{property.property_type}</Badge>
                  {property.is_featured && <Badge variant="secondary" className="text-xs">Featured</Badge>}
                </div>

                <div className="flex items-baseline gap-2 mb-1 flex-wrap min-w-0">
                  <span className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground break-words tracking-tight">
                    {formatPrice(property.price, property.currency || 'ETB')}
                  </span>
                  {property.listing_type === 'rent' && <span className="text-base sm:text-lg text-muted-foreground">/month</span>}
                </div>
                {property.area_sqm > 0 && property.listing_type === 'sale' && (
                  <div className="text-xs text-muted-foreground mb-2">
                    {formatPrice(Math.round(property.price / property.area_sqm), property.currency || 'ETB')} per m²
                  </div>
                )}

                <div className="flex items-start gap-1.5 text-muted-foreground mb-3 min-w-0">
                  <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm break-words min-w-0">
                    {property.city}{property.sub_city ? ` - ${property.sub_city}` : ''}{property.woreda ? `, ${property.woreda}` : ''}
                    {property.address ? `, ${property.address}` : ''}
                  </span>
                </div>

                <div className="flex items-center gap-x-6 gap-y-2 text-sm text-muted-foreground flex-wrap min-w-0">
                  {isPlot ? (
                    <>
                      {property.area_sqm != null && (
                        <span className="flex items-center gap-1.5">
                          <Maximize className="h-4 w-4" />
                          <strong className="text-foreground">{property.area_sqm} m²</strong>
                          <span className="text-xs">({(property.area_sqm * 10.764).toFixed(0)} sqft)</span>
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <Home className="h-4 w-4" />
                        <strong className="text-foreground capitalize">{property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}</strong>
                      </span>
                    </>
                  ) : (
                    <>
                      {property.bedrooms != null && (
                        <span className="flex items-center gap-1.5"><Bed className="h-4 w-4" /><strong className="text-foreground">{property.bedrooms}</strong> Beds</span>
                      )}
                      {property.bathrooms != null && (
                        <span className="flex items-center gap-1.5"><Bath className="h-4 w-4" /><strong className="text-foreground">{property.bathrooms}</strong> Baths</span>
                      )}
                      {property.area_sqm != null && (
                        <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4" /><strong className="text-foreground">{property.area_sqm}</strong> m²</span>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <Button variant="outline" size="sm" onClick={handleSave} className={saved ? 'text-red-500 border-red-200' : ''}>
                  <Heart className={`h-4 w-4 sm:mr-1.5 ${saved ? 'fill-red-500' : ''}`} />
                  <span className="hidden sm:inline">{saved ? 'Saved' : 'Save'}</span>
                </Button>
                <Button variant="outline" size="sm" onClick={handleShare}>
                  <Share2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Share</span>
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Print</span>
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-x-4 gap-y-1 mt-3 text-[11px] text-muted-foreground flex-wrap">
              {timeAgo && (
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Added {timeAgo}</span>
              )}
              {property.view_count != null && (
                <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{property.view_count} views</span>
              )}
              <span className="flex items-center gap-1 font-mono"><Copy className="h-3 w-3" />ID: {(property.reference_code || `ID${property.id?.slice(0, 8)}`).toUpperCase()}</span>
            </div>
          </div>

          <Separator className="mb-6" />

          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <div className="space-y-8">
              <PropertyDetailsSection property={property} title={title} description={description} isPlot={isPlot} />
              <NearbyAmenities latitude={property.latitude} longitude={property.longitude} />
            </div>
            <div className="order-first lg:order-last">
              <div className="lg:sticky lg:top-[100px]">
                <PropertyContactSidebar property={property} propertyId={id!} />
              </div>
            </div>
          </div>

          {similarProperties && similarProperties.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display text-xl font-bold mb-6">Similar Properties</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {similarProperties.map((p: any) => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= MOBILE / TABLET LAYOUT (<lg) ================= */}
      <div className="lg:hidden pb-20">
        {/* Image Gallery */}
        <PropertyImageGallery images={images} title={title} isFeatured={property.is_featured} />

        {/* Key Facts strip */}
        <div className="container px-4 pt-4">
          <KeyFactsStrip property={property} isPlot={isPlot} />
        </div>

        {/* Compact header */}
        <div className="container px-4 pt-4 pb-3">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="font-display text-2xl font-bold text-foreground break-words min-w-0">
              {formatPrice(property.price, property.currency || 'ETB')}
              {property.listing_type === 'rent' && <span className="text-sm text-muted-foreground font-normal">/month</span>}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={handleSave} aria-label="Save" className={`h-9 w-9 rounded-full border flex items-center justify-center ${saved ? 'text-red-500 border-red-200 bg-red-50' : 'text-muted-foreground'}`}>
                <Heart className={`h-4 w-4 ${saved ? 'fill-red-500' : ''}`} />
              </button>
              <button onClick={handleShare} aria-label="Share" className="h-9 w-9 rounded-full border flex items-center justify-center text-muted-foreground">
                <Share2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className="text-[10px] capitalize">{property.listing_type === 'sale' ? t('property.forSale') : t('property.forRent')}</Badge>
            <Badge variant="outline" className="text-[10px] capitalize">{property.property_type}</Badge>
            {property.is_featured && <Badge variant="secondary" className="text-[10px]">Featured</Badge>}
          </div>

          <div className="flex items-start gap-1.5 text-muted-foreground mb-3">
            <MapPin className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            <span className="text-xs break-words">
              {property.city}{property.sub_city ? ` - ${property.sub_city}` : ''}{property.woreda ? `, ${property.woreda}` : ''}
            </span>
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
            {!isPlot && property.bedrooms != null && (
              <span className="flex items-center gap-1.5"><Bed className="h-4 w-4" /><strong className="text-foreground">{property.bedrooms}</strong></span>
            )}
            {!isPlot && property.bathrooms != null && (
              <span className="flex items-center gap-1.5"><Bath className="h-4 w-4" /><strong className="text-foreground">{property.bathrooms}</strong></span>
            )}
            {property.area_sqm != null && (
              <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4" /><strong className="text-foreground">{property.area_sqm}</strong> m²</span>
            )}
          </div>
        </div>

        {/* Inline action bar */}
        <div className="container px-4 pb-3">
          <MobileContactActionBar
            phone={property.contact_phone || '+251911000000'}
            email={property.contact_email}
            variant="inline"
          />
        </div>

        {/* Sticky section tabs */}
        <MobileSectionTabs
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'nearby', label: 'Nearby' },
            ...(property.listing_type === 'rent' ? [] : [
              { id: 'home-finance', label: 'Home Finance' },
              { id: 'price-index', label: 'Price Index' },
            ]),
          ]}
        />

        {/* Stacked sections */}
        <div className="container px-4 pt-2">
          <PropertyDetailsSection
            property={property}
            title={title}
            description={description}
            isPlot={isPlot}
            mobileLayout
          />

          <div className="mt-6">
            <NearbyAmenities latitude={property.latitude} longitude={property.longitude} />
          </div>

          {/* Lead form */}
          <div className="mt-8">
            <MobileLeadFormSection listingId={id!} listingLabel="property" referenceCode={property.reference_code} />
          </div>

          {/* Bottom blocks */}
          <div className="mt-6 space-y-4">
            <MobileSafetyTips />
            <MobileUsefulLinks
              links={[
                { label: `Property for sale in ${property.city}`, to: `/buy?city=${property.city}` },
                ...(property.sub_city ? [{ label: `Property for sale in ${property.sub_city}`, to: `/buy?city=${property.city}&sub_city=${property.sub_city}` }] : []),
                { label: `Property for rent in ${property.city}`, to: `/rent?city=${property.city}` },
                { label: `${property.property_type}s for sale`, to: `/buy?property_type=${property.property_type}` },
              ]}
            />
            <MobileReportListing targetType="property" targetId={id!} label="Report this property" />
          </div>

          {/* Similar properties */}
          {similarProperties && similarProperties.length > 0 && (
            <div className="mt-10">
              <h2 className="font-display text-lg font-bold mb-4">Similar Properties</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {similarProperties.map((p: any) => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Fixed bottom action bar */}
        <MobileContactActionBar
          phone={property.contact_phone || '+251911000000'}
          email={property.contact_email}
          variant="fixed"
        />
      </div>
    </Layout>
  );
}
